import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { getPatientPrescription } from "../../SalesService";

export const usePrescriptionManager = ({
	t,
	loadMedicines,
	upsertCartItem,
	resetCart,
	getCartLength,
	setLoading,
	isHospitalConnected,
	customerType,
	pharmacyPOS,
	onMedicineSearch,
}) => {
	const [recentPrescriptions, setRecentPrescriptions] = useState([]);
	const [prescriptionDetails, setPrescriptionDetails] = useState(null);
	const [showPrescriptionsCard, setShowPrescriptionsCard] = useState(false);
	const [showPrescriptionOffcanvas, setShowPrescriptionOffcanvas] = useState(false);
	const [selectedPrescription, setSelectedPrescription] = useState(null);
	const [consultationOpen, setConsultationOpen] = useState(false);
	const [addedPrescriptions, setAddedPrescriptions] = useState(new Set());
	const [cartPrescriptionId, setCartPrescriptionId] = useState(null);

	const clearCartPrescription = useCallback(() => {
		setCartPrescriptionId(null);
		setPrescriptionDetails(null);
		setAddedPrescriptions(new Set());
	}, []);

	const loadRecentPrescriptions = useCallback(
		async (patientId) => {
			if (!patientId) return;

			setLoading(true);
			try {
				const body = {
					patient_id: patientId,
					isHospitalPatient: isHospitalConnected && customerType === "hospital_patient",
					pageNo: 1,
					pageSize: 5,
				};
				if (isHospitalConnected && customerType === "hospital_patient") {
					body.clinic_id = pharmacyPOS.hospital_id;
				}
				const response = await getPatientPrescription(body);
				const data = response.data?.data || {};
				let prescriptions = [];
				if (isHospitalConnected && customerType === "hospital_patient") {
					const consultations = Array.isArray(data) ? data : [];
					prescriptions = consultations;
					setPrescriptionDetails((prev) => {
						if (cartPrescriptionId) {
							return (
								consultations.find((item) => item._id === cartPrescriptionId) || prev
							);
						}
						return prev;
					});
				} else {
					prescriptions = (data.lastFewSales || []).map((sale) => ({
						_id: sale._id,
						createdAt: sale.createdAt,
						doctor: { name: "N/A" },
						prescriptions: sale.items?.map((item) => ({
							medication_name: item.medicineName,
							salt_composition: item.brandName,
							dosage_name: item.productForm,
							duration: "N/A",
							type: "Sale",
							qty: item.medicineQunatity,
							instruction: "",
						})),
					}));
				}
				setRecentPrescriptions(prescriptions);
				setShowPrescriptionsCard(true);
			} catch (error) {
				console.error("Error fetching recent prescriptions:", error);
				setRecentPrescriptions([]);
				setShowPrescriptionsCard(false);
			} finally {
				setLoading(false);
			}
		},
		[
			cartPrescriptionId,
			customerType,
			isHospitalConnected,
			pharmacyPOS?.hospital_id,
			setLoading,
		]
	);

	const handleSelectPrescription = useCallback((prescription) => {
		setSelectedPrescription(prescription);
		if (prescription && typeof prescription === "object" && Array.isArray(prescription.prescriptions)) {
			setPrescriptionDetails(prescription);
		}
		setShowPrescriptionOffcanvas(true);
	}, []);

	const toggleConsultation = useCallback(() => {
		setConsultationOpen((prev) => !prev);
	}, []);

	const addPrescriptionToCart = useCallback(
		async (prescription) => {
			if (!prescription) return;

			const prescriptionId = prescription._id;
			const cartLength = typeof getCartLength === "function" ? getCartLength() : 0;
			const isDifferentPrescription =
				prescriptionId &&
				cartPrescriptionId &&
				cartPrescriptionId !== prescriptionId &&
				cartLength > 0;

			if (isDifferentPrescription) {
				const result = await Swal.fire({
					title: t("Replace current prescription?"),
					text: t(
						"Only one prescription can be active in the cart at a time. Adding this prescription will replace medicines from the previous one."
					),
					icon: "warning",
					showCancelButton: true,
					confirmButtonText: t("Yes, replace"),
					cancelButtonText: t("Cancel"),
				});
				if (!result.isConfirmed) return;
				resetCart?.();
				setAddedPrescriptions(new Set());
				setCartPrescriptionId(null);
			}

			setLoading(true);
			try {
				let processed = false;
				let medications = [];

				if (Array.isArray(prescription)) {
					medications = prescription;
				} else if (prescription.items && Array.isArray(prescription.items)) {
					medications = prescription.items;
				} else if (prescription.prescriptions && Array.isArray(prescription.prescriptions)) {
					medications = prescription.prescriptions;
				} else if (prescription.medication_name || prescription.salt_composition) {
					medications = [prescription];
				} else if (prescription.medications && Array.isArray(prescription.medications)) {
					medications = prescription.medications;
				}

				for (const medication of medications) {
					let searchTerm = "";

					if (medication.salt_composition) {
						searchTerm = medication.salt_composition;
					} else if (medication.medication_name) {
						searchTerm = medication.medication_name;
					} else if (medication.medicineName) {
						searchTerm = medication.medicineName;
					} else if (medication.brandName) {
						searchTerm = medication.brandName;
					} else if (medication.name) {
						searchTerm = medication.name;
					} else if (medication.productName) {
						searchTerm = medication.productName;
					}

					if (!searchTerm) {
						console.warn("No search term found for medication:", medication);
						toast.info(t("Could not identify medication"));
						continue;
					}

					onMedicineSearch?.(searchTerm);

					const medicines = await loadMedicines(searchTerm);

					if (!medicines?.length) {
						const alternativeSearch = medication.medication_name || medication.medicineName;
						if (alternativeSearch && alternativeSearch !== searchTerm) {
							onMedicineSearch?.(alternativeSearch);
							const alternativeMedicines = await loadMedicines(alternativeSearch);

							if (alternativeMedicines?.length) {
								let product = alternativeMedicines[0].raw || alternativeMedicines[0];
								let batches = product.stockData || [];

								for (const med of alternativeMedicines) {
									const p = med.raw || med;
									const b = p.stockData || [];
									if (b.length > 0) {
										product = p;
										batches = b;
										break;
									}
								}

								if (!batches.length) {
									toast.info(
										t("No stock available for {{name}}", {
											name: medication.medication_name || searchTerm,
										})
									);
									continue;
								}

								const batch = batches[0];
								const prescribedQty =
									parseInt(medication.qty, 10) ||
									parseInt(medication.quantity, 10) ||
									parseInt(medication.dosage_qty, 10) ||
									1;

								upsertCartItem(product, batch, prescribedQty);
								processed = true;
								continue;
							}
						}

						toast.info(
							t("Product not found: {{name}}", {
								name: medication.medication_name || medication.medicineName || searchTerm,
							})
						);
						continue;
					}

					let product = medicines[0].raw || medicines[0];
					let batches = product.stockData || [];

					for (const med of medicines) {
						const p = med.raw || med;
						const b = p.stockData || [];
						if (b.length > 0) {
							product = p;
							batches = b;
							break;
						}
					}

					if (!batches.length) {
						toast.info(
							t("No stock available for {{name}}", {
								name: medication.medication_name || searchTerm,
							})
						);
						continue;
					}

					const batch = batches[0];
					const prescribedQty =
						parseInt(medication.qty, 10) ||
						parseInt(medication.quantity, 10) ||
						parseInt(medication.dosage_qty, 10) ||
						1;

					upsertCartItem(product, batch, prescribedQty);
					processed = true;
				}

				if (processed) {
					if (prescriptionId) {
						setCartPrescriptionId(prescriptionId);
						setAddedPrescriptions(new Set([prescriptionId]));
					}
					setPrescriptionDetails(prescription);
					setShowPrescriptionOffcanvas(false);
					setSelectedPrescription(null);
					// toast.success(t("Prescription medicines added to cart"));
				}
			} catch (error) {
				console.error("Error adding prescription to cart:", error);
				Swal.fire({
					icon: "error",
					title: t("Failed to add prescriptions"),
					text: t("We could not add the prescription items to the cart. Please try again."),
					confirmButtonText: t("OK"),
				});
			} finally {
				setLoading(false);
			}
		},
		[
			cartPrescriptionId,
			getCartLength,
			loadMedicines,
			onMedicineSearch,
			resetCart,
			setLoading,
			t,
			upsertCartItem,
		]
	);

	return {
		recentPrescriptions,
		showPrescriptionsCard,
		setShowPrescriptionsCard,
		showPrescriptionOffcanvas,
		setShowPrescriptionOffcanvas,
		selectedPrescription,
		setSelectedPrescription,
		consultationOpen,
		toggleConsultation,
		setConsultationOpen,
		addedPrescriptions,
		setAddedPrescriptions,
		cartPrescriptionId,
		clearCartPrescription,
		addPrescriptionToCart,
		loadRecentPrescriptions,
		handleSelectPrescription,
		setRecentPrescriptions,
		prescriptionDetails,
		setPrescriptionDetails,
	};
};
